import { useCallback } from 'react';
import { useDashboardAssistantDock } from '../contexts/DashboardAssistantDockContext';
import { getTimezoneOffset, getValidTimezone } from '../utils/formatTimestamp';
import { P } from '../utils/permissions';
import {
  NAV_OPEN_CONTACTS,
  NAV_OPEN_CREATE_CONTACT,
  NAV_OPEN_CREATE_ELEMENT,
  NAV_OPEN_CREATE_EVENT_FORM,
  NAV_OPEN_CREATE_FORM,
  NAV_OPEN_CREATE_PAYMENT,
  NAV_OPEN_DASHBOARD,
  NAV_OPEN_ELEMENTS,
  NAV_OPEN_FORMS,
  NAV_OPEN_HOME,
  NAV_OPEN_PAYMENTS,
  NAV_OPEN_PAYOUTS,
  NAV_OPEN_SETTINGS,
  NAV_OPEN_SUBSCRIPTIONS,
} from './useDashboardAssistantActions/data/navigationActions';
import {
  SET_OPEN_BILLING_SETTINGS,
  SET_OPEN_COMPLIANCE_SETTINGS,
  SET_OPEN_CUSTOM_INPUT_SETTINGS,
  SET_OPEN_EMAIL_SETTINGS,
  SET_OPEN_EMBED_SETTINGS,
  SET_OPEN_FAQ_SETTINGS,
  SET_OPEN_INTEGRATIONS_SETTINGS,
  SET_OPEN_PAYOUT_FREQUENCY_SETTINGS,
  SET_OPEN_PERMISSIONS_SETTINGS,
  SET_OPEN_PROFILE_SETTINGS,
  SET_OPEN_USERS_SETTINGS,
  SET_OPEN_CONTACT_PORTAL_SETTINGS,
} from './useDashboardAssistantActions/data/settingsActions';
import { useDashboardNavigationAction } from './useDashboardAssistantActions/hooks/useDashboardNavigationAction';
import { useRecordOpenAction } from './useDashboardAssistantActions/hooks/useRecordOpenAction';
import { useGetCurrentDashboardContextAction } from './useDashboardAssistantActions/hooks/useGetCurrentDashboardContextAction';
import { useSearchDashboardRecordsAction } from './useDashboardAssistantActions/hooks/useSearchDashboardRecordsAction';
import { useFindContactAction } from './useDashboardAssistantActions/hooks/useFindContactAction';
import { useGetContactDonationSummaryAction } from './useDashboardAssistantActions/hooks/useGetContactDonationSummaryAction';
import { useFindAndOpenContactAction } from './useDashboardAssistantActions/hooks/useFindAndOpenContactAction';
import { useGetLatestPayoutAction } from './useDashboardAssistantActions/hooks/useGetLatestPayoutAction';

export function useDashboardAssistantActions({
  currentFeature,
  dashboardTimezone,
  organizationId,
  organizationName,
  routeContext,
  userContext,
} = {}) {
  const { activeSessionId } = useDashboardAssistantDock();
  const normalizedDashboardTimezone = dashboardTimezone
    ? getValidTimezone(dashboardTimezone, 'America/Chicago')
    : 'America/Chicago';
  const dashboardTimezoneOffset = getTimezoneOffset(normalizedDashboardTimezone);

  // Dispatch a synthetic tool-result event so the assistant chat dock can render
  // the action inline. Bound to the currently active session.
  const dispatchActionMessage = useCallback((message) => {
    if (!activeSessionId || typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(
      new CustomEvent('dashboard-assistant-tool-result', {
        detail: {
          message,
          sessionId: activeSessionId,
        },
      }),
    );
  }, [activeSessionId]);
  const withToolDispatch = useCallback(
    (action) => ({ ...action, dispatchActionMessage }),
    [dispatchActionMessage],
  );

  // Register every navigation action with an explicit hook call so React's
  // Rules of Hooks are satisfied (no hook calls inside loops/conditionals).
  // The static arrays preserve action data; if entries are added or removed,
  // the corresponding hook call below must be updated to match.
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_DASHBOARD));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_HOME));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_PAYMENTS));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_CONTACTS));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_SUBSCRIPTIONS));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_FORMS));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_ELEMENTS));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_PAYOUTS));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_CREATE_CONTACT));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_CREATE_PAYMENT));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_CREATE_FORM));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_CREATE_EVENT_FORM));
  useDashboardNavigationAction(withToolDispatch(NAV_OPEN_CREATE_ELEMENT));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_PROFILE_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_BILLING_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_USERS_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_PERMISSIONS_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_EMBED_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_FAQ_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_CUSTOM_INPUT_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_EMAIL_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_COMPLIANCE_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_PAYOUT_FREQUENCY_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_INTEGRATIONS_SETTINGS));
  useDashboardNavigationAction(withToolDispatch(SET_OPEN_CONTACT_PORTAL_SETTINGS));

  useRecordOpenAction({
    name: 'open_contact',
    description: 'Open a contact record by contact ID.',
    parameterName: 'contactId',
    parameterDescription:
      'The exact CharityStack contactId returned by search_dashboard_records or find_contact. Do not derive this from a userId, name, email, or route context.',
    getPath: (contactId) => `/contacts?contact=${contactId}&noSheet=1`,
    dispatchActionMessage,
    permission: P.VIEW_CONTACTS,
    validateRecordType: 'contact',
  });

  useRecordOpenAction({
    name: 'open_payment_contact',
    description: 'Open the payments workspace filtered to a contact ID.',
    parameterName: 'contactId',
    parameterDescription:
      'The exact CharityStack contactId returned by search_dashboard_records or find_contact. Do not derive this from a userId, name, email, or route context.',
    getPath: (contactId) => `/payments?contact=${contactId}`,
    dispatchActionMessage,
    permission: P.VIEW_PAYMENTS,
    validateRecordType: 'contact',
  });

  useRecordOpenAction({
    name: 'open_subscription_contact',
    description: 'Open the subscriptions workspace filtered to a contact ID.',
    parameterName: 'contactId',
    parameterDescription:
      'The exact CharityStack contactId returned by search_dashboard_records or find_contact. Do not derive this from a userId, name, email, or route context.',
    getPath: (contactId) => `/subscriptions?contact=${contactId}`,
    dispatchActionMessage,
    permission: P.VIEW_SUBSCRIPTIONS,
    validateRecordType: 'contact',
  });

  useRecordOpenAction({
    name: 'open_form',
    description: 'Open a form or fundraiser record by form ID.',
    parameterName: 'formId',
    parameterDescription:
      'The exact CharityStack formId returned by search_dashboard_records. Do not derive this from a form name or route context.',
    getPath: (formId) => `/forms/${formId}?noSheet=1`,
    dispatchActionMessage,
    permission: P.VIEW_FORMS,
    validateRecordType: 'form',
  });

  useRecordOpenAction({
    name: 'open_element',
    description: 'Open an embeddable element record by element ID.',
    parameterName: 'elementId',
    parameterDescription:
      'The exact CharityStack elementId returned by search_dashboard_records. Do not derive this from an element name or route context.',
    getPath: (elementId) => `/elements/${elementId}?noSheet=1`,
    dispatchActionMessage,
    permission: P.VIEW_ELEMENTS,
    validateRecordType: 'element',
  });

  useGetCurrentDashboardContextAction({
    currentFeature,
    dispatchActionMessage,
    organizationId,
    organizationName,
    routeContext,
    userContext,
  });

  useSearchDashboardRecordsAction({
    activeSessionId,
    dashboardTimezoneOffset,
    dispatchActionMessage,
    normalizedDashboardTimezone,
    organizationName,
  });

  useFindContactAction({
    activeSessionId,
    dispatchActionMessage,
    organizationName,
  });

  useGetContactDonationSummaryAction({
    activeSessionId,
    dashboardTimezoneOffset,
    dispatchActionMessage,
    normalizedDashboardTimezone,
    organizationName,
  });

  useGetLatestPayoutAction({
    activeSessionId,
    dispatchActionMessage,
    organizationName,
  });

  useFindAndOpenContactAction({
    activeSessionId,
    dispatchActionMessage,
    organizationName,
  });
}

export default useDashboardAssistantActions;
